import {
  ErrorBoundary,
  GlobalHeader,
  postClose,
  PullToRefresh,
  useConfigContext,
  useDebugDrawer,
  useDialogContext,
  useUserContext,
  WarmingUp,
} from "@WahooFitness/wahoo-offline-mfe";
import { Box, Container, Paper, Switch, Typography } from "@mui/material";
import {
  MenuList,
  MenuListItemVariant,
  WfIconContentExternalLinkRegular,
  WfIconContentInfoRegular,
  WfIconNavigationCloseRegular,
} from "@WahooFitness/redesignr";
import React, { useCallback, useMemo } from "react";
import AuthorizedApp from "./AuthorizedApp";
import RecoverySourceApp from "./RecoverySourceApp";
import AppleHealthRecoveryElsewhere from "./AppleHealthRecoveryElsewhere";
import { APPLE_HEALTH_FITNESS_APP_ID_BY_ENVIRONMENT } from "./useNativeHealth";
import { WAHOO_APP_ANDROID_FITNESS_APP_ID } from "./wahooAppFitnessAppIds";
import useThirdPartySharing from "./useThirdPartySharing";
import { refreshRecoveryHealthSources } from "./useRecoveryHealthSources";
import VitalSummarySyncToast from "./VitalSummarySyncToast";
import RequestWearablePrompt from "./RequestWearablePrompt";
import { useThirdPartySharingDebug } from "./useThirdPartySharingDebug";
import { Trans, t } from "@lingui/macro";
import { useSettingsRemoteConfig } from "@/hooks/useSettingsRemoteConfig";
import { useSettingsVersionedFeatures } from "@/hooks/useSettingsVersionedFeatures";
import useUserAccountSettings from "@/hooks/useUserAccountSettings";
import { ConnectedAppUserType } from "@WahooFitness/cloud-client-types";
import AppleHealthIcon from "@/assets/AppleHealth.png";

const ThirdPartySharing: React.FC<{
  closeOnBack: boolean;
  filterByCapability?: keyof ConnectedAppUserType;
}> = ({ closeOnBack, filterByCapability }) => {
  const {
    connectedAppsLoading,
    connectedAppsError,
    mutateConnectedApps,
    availableApps,
    appleHealthComingSoon,
    authorizedApps,
    emailAuthorized,
    authorizeEmail,
    deauthorizeEmail,
    emailSharingUpdateInProgress,
    recovery247Enabled,
    appleHealthRecoverySourceEnabled,
  } = useThirdPartySharing(filterByCapability);
  const { getSettingsRemoteConfigString } = useSettingsRemoteConfig();

  const { enabled: debugDrawerEnabled } = useSettingsVersionedFeatures("DEBUG_DRAWER_FLAG");
  const debugSections = useThirdPartySharingDebug(debugDrawerEnabled);
  const { debugTapTarget } = useDebugDrawer({
    enabled: debugDrawerEnabled,
    sections: debugSections,
  });

  const { setDialog } = useDialogContext();

  const { userAccountSettingsData, reloadUserAccountSettings } = useUserAccountSettings();
  const activeFitnessAppId = userAccountSettingsData?.recovery_fitness_app_id;

  // Android has no Apple Health row of its own, so an account reading recovery from Apple Health on
  // another phone would show no source at all: a read-only card shows it instead (TAJ-1641).
  const { fitnessAppId, environment } = useConfigContext();
  const appleHealthOnAnotherDevice =
    filterByCapability === "import_vitals" &&
    recovery247Enabled &&
    appleHealthRecoverySourceEnabled &&
    fitnessAppId === WAHOO_APP_ANDROID_FITNESS_APP_ID &&
    activeFitnessAppId != null &&
    activeFitnessAppId === APPLE_HEALTH_FITNESS_APP_ID_BY_ENVIRONMENT[environment];

  // Named in the confirmation when a toggle would replace it. Undefined when the active source
  // isn't in this list (capability revoked, or Apple Health behind its flag). Apple Health has no
  // connected_apps record, so it is named here when the card above shows it.
  const currentSourceName = useMemo(() => {
    if (appleHealthOnAnotherDevice) {
      return t`Apple Health`;
    }
    const active = activeFitnessAppId
      ? authorizedApps.find((app) => app.fitnessAppId === activeFitnessAppId)
      : undefined;
    return active ? String(active.content) : undefined;
  }, [activeFitnessAppId, appleHealthOnAnotherDevice, authorizedApps]);

  const openInfoDialog = useCallback(() => {
    setDialog({
      open: true,
      title: t`About authorized apps`,
      body: t`Authorizing select third party apps may boost functionality to your account. Some external apps offer extra privacy settings when sharing your data`,
      actions: [{ text: t`Got it` }],
    });
  }, [setDialog]);

  const handleReload = useCallback(() => {
    mutateConnectedApps();
    // The recovery source may have been switched on another phone.
    reloadUserAccountSettings();
    // The Apple Health HRV device may have been changed on another phone: native syncs and answers again.
    if (recovery247Enabled && appleHealthRecoverySourceEnabled) {
      refreshRecoveryHealthSources();
    }
  }, [
    appleHealthRecoverySourceEnabled,
    mutateConnectedApps,
    recovery247Enabled,
    reloadUserAccountSettings,
  ]);

  const closeAction = useCallback(() => {
    postClose("thirdPartySharing");
  }, []);

  const handleEmailToggle = useCallback(
    (_: any, checked: boolean) => {
      if (emailSharingUpdateInProgress) {
        return;
      }
      if (checked) {
        authorizeEmail();
      } else {
        deauthorizeEmail();
      }
    },
    [authorizeEmail, deauthorizeEmail, emailSharingUpdateInProgress]
  );

  const { currentUser } = useUserContext();

  return (
    <Container
      maxWidth="sm"
      disableGutters
      // A column, so the wearable request prompt can sit at the bottom of a short list.
      sx={{ minHeight: "100%", display: "flex", flexDirection: "column" }}
    >
      <ErrorBoundary
        isLoading={connectedAppsLoading}
        error={connectedAppsError}
        onReload={handleReload}
      >
        <PullToRefresh onRefresh={handleReload} />
        <GlobalHeader
          title={t`Authorized apps`}
          primaryRightActionItem={{
            icon: <WfIconContentInfoRegular />,
            action: openInfoDialog,
          }}
          primaryLeftActionItem={
            closeOnBack
              ? {
                  icon: <WfIconNavigationCloseRegular />,
                  action: closeAction,
                }
              : undefined
          }
        />
        {debugTapTarget}
        <Box display="flex" alignItems="center" justifyContent="center" p={2}>
          <Typography variant="prose-sm" color="text.secondary" align="center">
            <Trans>
              Unlock more features and enhance your experience by authorizing third-party services.
            </Trans>
          </Typography>
        </Box>
        {connectedAppsLoading ? (
          <WarmingUp />
        ) : (
          <>
            {!filterByCapability && (
              <Box px={2}>
                <Paper elevation={4}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    py={1}
                    px={2}
                  >
                    <Box display="flex" flexDirection="column" justifyContent="center">
                      <Typography variant="prose-base">{t`Email activities`}</Typography>
                      <Typography variant="prose-sm">{currentUser?.email}</Typography>
                    </Box>
                    <Switch
                      checked={emailAuthorized}
                      onChange={handleEmailToggle}
                      color="info"
                      disabled={emailSharingUpdateInProgress}
                    />
                  </Box>
                </Paper>
              </Box>
            )}
            <Box display="flex" flexDirection="column" gap={1} px={2} pt={1} pb={3}>
              {appleHealthOnAnotherDevice && <AppleHealthRecoveryElsewhere />}
              {authorizedApps.map((app) =>
                filterByCapability ? (
                  <RecoverySourceApp key={app.id} {...app} currentSourceName={currentSourceName} />
                ) : (
                  <AuthorizedApp key={app.id} {...app} currentSourceName={currentSourceName} />
                )
              )}
            </Box>
            {availableApps.length > 0 && (
              <>
                <Box mx={2}>
                  <Typography variant="prose-md-bold">
                    <Trans>Available apps</Trans>
                  </Typography>
                </Box>
                <MenuList listItems={availableApps} />
              </>
            )}
            {appleHealthComingSoon && (
              <Box data-testid="thirdPartySharing.appleHealthComingSoon">
                <Box mx={2} mt={2}>
                  <Typography variant="prose-md-bold">
                    <Trans>Coming soon</Trans>
                  </Typography>
                </Box>
                <MenuList
                  listItems={[
                    {
                      id: "appleHealthComingSoon",
                      variant: MenuListItemVariant.Action,
                      disabled: true,
                      // Without an action MenuList renders a plain row that ignores `disabled`.
                      action: () => {},
                      icon: (
                        <Box
                          overflow="hidden"
                          borderRadius={2}
                          border={1}
                          borderColor="divider"
                          height={24}
                          width={24}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <img width="24" height="24" alt="" src={AppleHealthIcon} />
                        </Box>
                      ),
                      content: t`Apple Health`,
                      actionComponent: (
                        <WfIconContentExternalLinkRegular sx={{ color: "action.active" }} />
                      ),
                    },
                  ]}
                />
              </Box>
            )}
            {!filterByCapability && (
              <Box my={2}>
                <MenuList
                  listItems={[
                    {
                      id: "FullListSupportedPartners",
                      variant: MenuListItemVariant.ExternalLink,
                      content: t`Full list of supported partners`,
                      linkLocation: getSettingsRemoteConfigString("SUPPORTED_PARTNERS_PAGE_URL"),
                    },
                  ]}
                />
              </Box>
            )}
          </>
        )}
      </ErrorBoundary>
      {filterByCapability === "import_vitals" && !connectedAppsLoading && !connectedAppsError && (
        <RequestWearablePrompt />
      )}
      {filterByCapability === "import_vitals" && <VitalSummarySyncToast />}
    </Container>
  );
};

export default ThirdPartySharing;
